package org.matthewo.serviceworker

import io.ktor.client.call.*
import io.ktor.client.plugins.resources.*
import io.ktor.client.request.*
import io.ktor.http.*
import js.buffer.ArrayBuffer
import js.objects.unsafeJso
import js.promise.Promise
import js.promise.await
import js.reflect.Reflect
import js.typedarrays.Uint8Array
import org.matthewo.api.PushFollow
import org.matthewo.api.PushSubscription
import org.matthewo.api.PushFollowing
import org.matthewo.api.PushSettings
import org.matthewo.client
import org.matthewo.config.CONFIG
import org.matthewo.routes.API
import web.encoding.atob
import web.navigator.navigator
import web.notifications.Notification
import web.notifications.NotificationPermission
import web.notifications.denied
import web.notifications.granted
import web.push.getSubscription
import web.push.subscribe
import web.serviceworker.ServiceWorkerRegistration
import web.window.window
import web.push.PushSubscription as BrowserPushSubscription

/**
 * Following events on this device, through the browser's push service. There's no account: the browser's
 * subscription is the device, and the server keeps which events it follows.
 */
/**
 * A team that can't be followed at an event, since it isn't there.
 */
class NotAtEventException(val team: Int?) : Exception("Team $team isn't at the event")

object Notifications {
    enum class TestResult {
        SENT,

        // the device doesn't follow the event, or its push service forgot it
        NOT_FOLLOWING,

        // one was sent too recently
        TOO_SOON,
        FAILED,
    }

    /**
     * Whether this browser can be sent notifications and the server can send them. Safari on iPhone and iPad only
     * can once the site's been added to the Home Screen.
     */
    val supported: Boolean
        get() = null != CONFIG.WEB_PUSH_KEY
                && Reflect.has(navigator, "serviceWorker")
                && Reflect.has(window, "PushManager")
                && Reflect.has(window, "Notification")

    /**
     * Whether notifications were blocked for the site, which only the browser's settings can undo.
     */
    val blocked: Boolean get() = supported && NotificationPermission.denied == Notification.permission

    /**
     * What this device follows at the event, without asking for permission, or null when it's never followed anything
     * on this browser.
     */
    suspend fun following(year: Int, code: String): PushFollowing? {
        if (!supported || NotificationPermission.granted != Notification.permission) return null
        val subscription = registration().pushManager.getSubscription() ?: return null
        return send(year, code, PushFollow(subscription.serialize()))
    }

    /**
     * Asks for permission to show notifications, if it's never been given. It has to be called from the click itself,
     * not after anything it waits on, or Firefox and Safari refuse to ask.
     */
    fun askPermission(): Promise<NotificationPermission> = Notification.requestPermissionAsync()

    /**
     * Starts this device following [team] at the event with [settings] once [permission] is given, and gives what it
     * follows there now, or null without permission.
     */
    suspend fun follow(year: Int, code: String, team: Int, settings: PushSettings, permission: Promise<NotificationPermission>): PushFollowing? {
        if (!supported || NotificationPermission.granted != permission.await()) return null
        val key = CONFIG.WEB_PUSH_KEY ?: return null
        val pushManager = registration().pushManager
        val subscription = pushManager.getSubscription() ?: pushManager.subscribe(unsafeJso {
            // every message shows a notification, which browsers require
            userVisibleOnly = true
            applicationServerKey = fromBase64Url(key)
        })
        return send(year, code, PushFollow(subscription.serialize(), team, true, settings))
    }

    /**
     * Stops this device following [team] at the event, and gives what it follows there now. [settings] are sent too,
     * so they're kept for the page even if that was the last team, and the server forgets the device.
     */
    suspend fun unfollow(year: Int, code: String, team: Int, settings: PushSettings): PushFollowing? {
        if (!supported) return null
        val subscription = registration().pushManager.getSubscription() ?: return null
        return send(year, code, PushFollow(subscription.serialize(), team, false, settings))
    }

    /**
     * Changes which notifications this device gets about the teams it follows, and gives what it follows at the event
     * now, or null when it's never followed anything on this browser, so there's nothing to change yet.
     */
    suspend fun changeSettings(year: Int, code: String, settings: PushSettings): PushFollowing? {
        if (!supported || NotificationPermission.granted != Notification.permission) return null
        val subscription = registration().pushManager.getSubscription() ?: return null
        return send(year, code, PushFollow(subscription.serialize(), settings = settings))
    }

    /**
     * Has the server send this device a notification, the way it sends the event's results, to show they'll arrive.
     */
    suspend fun test(year: Int, code: String): TestResult {
        if (!supported || NotificationPermission.granted != Notification.permission) return TestResult.NOT_FOLLOWING
        val subscription = registration().pushManager.getSubscription() ?: return TestResult.NOT_FOLLOWING
        val response = client.post(API.Event.Push.Test(year, code)) {
            contentType(ContentType.Application.Cbor)
            setBody(subscription.serialize())
        }
        return when (response.status) {
            HttpStatusCode.NoContent -> TestResult.SENT
            HttpStatusCode.NotFound -> TestResult.NOT_FOLLOWING
            HttpStatusCode.TooManyRequests -> TestResult.TOO_SOON
            else -> TestResult.FAILED
        }
    }

    private suspend fun registration(): ServiceWorkerRegistration = navigator.serviceWorker.ready.await()

    private suspend fun send(year: Int, code: String, request: PushFollow): PushFollowing {
        val response = client.post(API.Event.Push(year, code)) {
            contentType(ContentType.Application.Cbor)
            setBody(request)
        }
        if (HttpStatusCode.UnprocessableEntity == response.status) throw NotAtEventException(request.team)
        if (!response.status.isSuccess()) error("Couldn't change notifications for $year $code: ${response.status}")
        return response.body<PushFollowing>()
    }

    private fun BrowserPushSubscription.serialize(): PushSubscription {
        val keys = toJSON().asDynamic().keys
        return PushSubscription(endpoint, keys.p256dh as String, keys.auth as String)
    }

    // Uint8Array.fromBase64 does this, but only in browsers from the last year or two
    private fun fromBase64Url(text: String): Uint8Array<ArrayBuffer> {
        val base64 = text.replace('-', '+').replace('_', '/')
        val binary = atob(base64.padEnd((base64.length + 3) / 4 * 4, '='))
        val bytes = Uint8Array<ArrayBuffer>(binary.length)
        val elements = bytes.asDynamic()
        binary.forEachIndexed { index, char -> elements[index] = char.code }
        return bytes
    }
}
